import { type InfiniteData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Endpoint } from '@/Config/api/endpoint';
import { SocketKey } from '@/Config/api/socketKey';
import i18n from '@/Config/app/i18n';
import { ConversationApi } from '@/Domain/api/Conversation/Conversation.api';
import { Event } from '@/Domain/api/Conversation/dto/Event.dto';
import { Paginated } from '@/Domain/api/Shared/dto/Paginated.dto';
import { UserOptionsApi } from '@/Domain/api/User/UserOptions.api';
import { InboxTranslationRequest } from '@/Domain/api/User/request/InboxTranslation.request';
import { useSocket } from '@/Domain/components/SocketProvider';
import { TranslationLanguagesEnum } from '@/Domain/enum/TranslatedLanguage.enum';

type TranslationInProgressSocket = {
  scenario_id: string;
  conversation_id: string;
  event: {
    translation_in_progress: boolean;
    error: any;
  };
};

type Props = {
  conversationId?: string;
};

export const LANGUAGES_OPTIONS = [
  {
    label: i18n.t('languages:en_GB'),
    value: TranslationLanguagesEnum.en_GB,
  },
  {
    label: i18n.t('languages:de_DE'),
    value: TranslationLanguagesEnum.de_DE,
  },
  {
    label: i18n.t('languages:es_ES'),
    value: TranslationLanguagesEnum.es_ES,
  },
  {
    label: i18n.t('languages:fr_FR'),
    value: TranslationLanguagesEnum.fr_FR,
  },
  {
    label: i18n.t('languages:it_IT'),
    value: TranslationLanguagesEnum.it_IT,
  },
  {
    label: i18n.t('languages:pl_PL'),
    value: TranslationLanguagesEnum.pl_PL,
  },
];

export const DEFAULT_LANGUAGE_OPTION = 'original' as const;
export const useConversationTranslation = ({ conversationId }: Props) => {
  const { t } = useTranslation('inbox_v2');
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  const params = useParams();

  const [translationInProgress, setIsTranslationInProgress] = useState<{
    conversationId: string | null;
    isTranslating: boolean;
  }>({
    conversationId: null,
    isTranslating: false,
  });

  const { data, isLoading } = useQuery({
    queryKey: [Endpoint.userOptions.inboxTranslation],
    queryFn: () => UserOptionsApi.getTranslation(),
  });

  const userTranslationMutation = useMutation({
    mutationKey: [`${Endpoint.userOptions.inboxTranslation}_patch`, conversationId],
    mutationFn: (data: InboxTranslationRequest) => {
      if (!data.inbox_translation_language) {
        void queryClient.setQueryData(
          ['conversations', conversationId, 'events'],
          (oldData: InfiniteData<Paginated<Event[]>>) => {
            if (!oldData) return undefined;

            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                items: page.items.map((item) => ({
                  ...item,
                  data: {
                    ...item.data,
                    content_translated: null,
                  },
                })),
              })),
            };
          },
        );
      }

      queryClient.setQueryData([Endpoint.userOptions.inboxTranslation], () => data);

      return UserOptionsApi.changeTranslation(data);
    },
    onSuccess: (_, variables) => {
      if (variables.inbox_translation_language) {
        return conversationTranslationMutation.mutate(variables);
      }
    },
    onError: () => {
      queryClient.setQueryData([Endpoint.userOptions.inboxTranslation], (prevData) => prevData);
      toast.error(t('toast.translation_change_error'));
    },
  });

  const conversationTranslationMutation = useMutation({
    mutationKey: [`${Endpoint.conversation.translate}_post`, conversationId],
    mutationFn: (data: InboxTranslationRequest) =>
      ConversationApi.translate(conversationId!, {
        language_to_translate: data.inbox_translation_language,
      }),
    onSuccess: (data, variables) => {
      const newItems = data.events.items;

      if (variables.inbox_translation_language) {
        void queryClient.setQueryData(
          ['conversations', conversationId, 'events'],
          (oldData: InfiniteData<Paginated<Event[]>>) => {
            if (!oldData) return undefined;

            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                items: newItems,
              })),
            };
          },
        );
      }
    },
  });

  const handelChangeLanguage = (
    language: TranslationLanguagesEnum | typeof DEFAULT_LANGUAGE_OPTION,
  ) => {
    if (language === DEFAULT_LANGUAGE_OPTION) {
      userTranslationMutation.mutate({ inbox_translation_language: null });

      return;
    }

    userTranslationMutation.mutate({ inbox_translation_language: language });
  };

  useEffect(() => {
    socket.on(
      SocketKey.conversation.translationInProgress,
      (event: TranslationInProgressSocket) => {
        if (event.conversation_id === params.id) {
          setIsTranslationInProgress({
            conversationId: event.conversation_id,
            isTranslating: event.event.translation_in_progress,
          });
        }
      },
    );
  }, [conversationId]);

  useEffect(() => {
    setIsTranslationInProgress({ conversationId: null, isTranslating: false });
  }, [conversationId]);

  return {
    isLoading,
    isTranslationInProgress: translationInProgress,
    language: data?.inbox_translation_language || null,
    handelChangeLanguage,
    conversationTranslationMutation,
    DEFAULT_LANGUAGE_OPTION,
    LANGUAGES_OPTIONS,
  };
};
